/**
 * DescriptionTemplateGuideModal (renomeado conceitualmente para
 * "Instruções de descrição") — ensina o admin a escrever instruções em
 * linguagem natural que a IA usa para compor a descrição do agendamento
 * no calendário, consumindo automaticamente os dados da conversa.
 *
 * O nome do arquivo/componente foi mantido por compatibilidade com o import
 * em CapabilityFields.js; o conteúdo é totalmente novo.
 */

import { Modal, Button } from 'react-bootstrap';
import {
  IconFileDescription,
  IconBulb,
  IconAlertTriangle,
  IconCircleCheck,
  IconSparkles,
} from '@tabler/icons-react';

const SectionTitle = ({ children }) => (
  <h5 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '20px 0 8px 0' }}>
    {children}
  </h5>
);

const Callout = ({ variant = 'info', icon, children }) => {
  const palettes = {
    info:    { bg: '#eff6ff', border: '#bfdbfe', color: '#1e40af' },
    success: { bg: '#f0fdf4', border: '#bbf7d0', color: '#166534' },
    warning: { bg: '#fffbeb', border: '#fde68a', color: '#92400e' },
  };
  const p = palettes[variant] || palettes.info;
  return (
    <div style={{
      padding: 12,
      borderRadius: 8,
      backgroundColor: p.bg,
      border: `1px solid ${p.border}`,
      color: p.color,
      display: 'flex',
      alignItems: 'flex-start',
      gap: 10,
      margin: '12px 0',
      fontSize: 13,
      lineHeight: 1.5,
    }}>
      {icon && <span style={{ flexShrink: 0, marginTop: 2 }}>{icon}</span>}
      <div>{children}</div>
    </div>
  );
};

const ExampleBlock = ({ title, instructions, output }) => (
  <div style={{
    padding: 12,
    borderRadius: 8,
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    marginBottom: 12,
  }}>
    <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 8 }}>
      {title}
    </div>
    <div style={{ fontSize: 11.5, color: '#64748b', marginBottom: 4 }}>
      O que o admin escreve no campo:
    </div>
    <div style={{
      background: '#fff',
      border: '1px dashed #cbd5e1',
      borderRadius: 6,
      padding: '8px 10px',
      fontSize: 12.5,
      color: '#1e293b',
      whiteSpace: 'pre-wrap',
      marginBottom: 10,
      fontFamily: 'inherit',
    }}>
      {instructions}
    </div>
    <div style={{ fontSize: 11.5, color: '#64748b', marginBottom: 4 }}>
      Descrição que a IA gera no agendamento:
    </div>
    <div style={{
      background: '#0f172a',
      color: '#e2e8f0',
      borderRadius: 6,
      padding: '10px 12px',
      fontSize: 12,
      lineHeight: 1.55,
      whiteSpace: 'pre-wrap',
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    }}>
      {output}
    </div>
  </div>
);

const DescriptionTemplateGuideModal = ({ show, onHide }) => {
  return (
    <Modal show={show} onHide={onHide} centered size="lg" scrollable>
      <Modal.Header closeButton style={{ borderBottom: '2px solid #6366f1' }}>
        <Modal.Title style={{ fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <IconFileDescription size={22} color="#6366f1" />
          Instruções para a IA gerar a descrição do agendamento
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ fontSize: 13.5, lineHeight: 1.55, color: '#334155' }}>
        {/* ============ O QUE É ============ */}
        <SectionTitle>Como funciona?</SectionTitle>
        <p>
          Neste campo você escreve, <strong>em português natural</strong>, o que quer ver na descrição
          do agendamento que é salva no calendário. A IA lê tudo o que foi coletado durante a
          conversa com o cliente e monta a descrição sozinha, seguindo as suas instruções.
        </p>

        <Callout variant="success" icon={<IconSparkles size={16} />}>
          <strong>Você NÃO precisa saber o nome de nenhuma variável.</strong> Nada de{' '}
          <code style={{ background: 'rgba(0,0,0,0.1)', padding: '1px 5px', borderRadius: 4 }}>
            {'{{campo}}'}
          </code>, nada de sintaxe técnica. Só escreva o que você quer — a IA se vira com os dados da conversa.
        </Callout>

        {/* ============ COMO USAR ============ */}
        <SectionTitle>Como escrever uma boa instrução</SectionTitle>
        <ol style={{ paddingLeft: 20, margin: '8px 0' }}>
          <li>
            <strong>Liste o que você quer ver na descrição</strong> — use bullets, frases curtas ou
            tópicos. Ex: "Inclua o motivo da consulta, o plano de saúde e o valor do sinal pago."
          </li>
          <li>
            <strong>Diga o tom que você quer</strong> — formal, informal, curto, detalhado,
            em bullets ou em parágrafo.
          </li>
          <li>
            <strong>Peça para a IA omitir o que não existe</strong> — isso impede que ela invente
            dados. O template padrão já faz isso por você.
          </li>
        </ol>

        {/* ============ EXEMPLOS ============ */}
        <SectionTitle>Exemplos prontos por nicho</SectionTitle>

        <ExampleBlock
          title="💆 Clínica de estética (cobra sinal antes)"
          instructions={`Inclua:
- Resumo do agendamento (cliente, data, horário, profissional, procedimento).
- Valor e método do sinal pago, caso o cliente tenha enviado comprovante.
- Alergias ou condições de saúde informadas.
- Observações adicionais do cliente.

Tom formal e direto. Omita o que não foi coletado.`}
          output={`Agendamento de limpeza de pele facial para Maria Silva em 20/04/2026 às 14:00 com Dra. Carolina.
Sinal de R$ 100,00 pago via PIX (comprovante recebido e validado).
Cliente relatou pele sensível e alergia a ácido salicílico.
Observação: prefere ser atendida na sala 2.`}
        />

        <ExampleBlock
          title="🏥 Consultório médico"
          instructions={`Gere uma descrição profissional contendo:
- Dados principais do agendamento (paciente, data, horário, profissional, tipo de consulta).
- Plano de saúde e número da carteirinha, se informados.
- Motivo da consulta ou sintomas relatados.
- Medicamentos ou alergias mencionados.

Use linguagem clara e técnica. Se algum dado não foi coletado, omita.`}
          output={`Consulta de cardiologia agendada para João Pereira em 22/04/2026 às 09:30 com Dr. Ricardo.
Plano: Unimed | Carteirinha: 123456789.
Paciente relata dor no peito há 3 dias e uso contínuo de Losartana 50mg.`}
        />

        <ExampleBlock
          title="🐶 Pet shop / Banho e tosa"
          instructions={`Monte a descrição com:
- Nome do pet, raça e peso (se informado).
- Serviço contratado e data/horário.
- Observações do tutor (comportamento, alergias, preferências).

Tom informal e acolhedor.`}
          output={`Banho e tosa de Thor (Golden Retriever, 32kg) agendado para 18/04/2026 às 10:00.
Tutor pediu para evitar tosa no pescoço — Thor fica estressado. Alergia a shampoo com perfume forte.`}
        />

        <ExampleBlock
          title="⚖️ Advocacia"
          instructions={`Registre:
- Dados do cliente, data, horário e advogado responsável.
- Assunto da consulta ou número do processo, se mencionado.
- Nível de urgência informado.

Seja formal e objetivo.`}
          output={`Consulta jurídica com Ana Costa agendada para 25/04/2026 às 15:00 com Dr. Paulo.
Assunto: revisão de contrato de locação residencial. Urgência: alta (cliente precisa assinar até o final da semana).`}
        />

        {/* ============ REGRAS ============ */}
        <SectionTitle>Garantias da IA</SectionTitle>

        <Callout variant="success" icon={<IconCircleCheck size={16} />}>
          <strong>A IA NÃO inventa dados.</strong> Se você pedir "inclua o plano de saúde" mas o
          cliente nunca informou o plano durante a conversa, a IA simplesmente omite essa parte.
          Nada de "plano: não informado" ou "plano: [desconhecido]".
        </Callout>

        <Callout variant="success" icon={<IconCircleCheck size={16} />}>
          <strong>A IA lê 100% do que foi coletado.</strong> Tudo que a IA ou as automações do seu
          fluxo salvaram durante o atendimento (nome, telefone, CPF, valor do sinal, URL do
          comprovante, observações, etc.) entra automaticamente na composição. Você não precisa
          listar campo por campo.
        </Callout>

        <Callout variant="warning" icon={<IconAlertTriangle size={16} />}>
          <strong>Seja específico no que pedir.</strong> "Inclua tudo" resulta em descrições
          longas e ruidosas. Diga exatamente quais categorias de informação importam para você
          (pagamento, plano de saúde, observações, etc.).
        </Callout>

        {/* ============ FALLBACKS ============ */}
        <SectionTitle>E se o campo ficar vazio?</SectionTitle>
        <p>
          Tudo certo — a IA volta ao comportamento padrão e gera uma descrição curta com os dados
          básicos do agendamento (cliente, data, horário, profissional, contato). É um resumo seguro
          que serve para a maioria dos casos sem configuração nenhuma.
        </p>

        {/* ============ DICA FINAL ============ */}
        <Callout variant="info" icon={<IconBulb size={16} />}>
          <strong>Dica final:</strong> comece com o template padrão que já veio preenchido, rode
          uns atendimentos de teste, abra o evento no calendário e veja como a descrição fica.
          Se faltar alguma coisa, só adicionar uma linha nas suas instruções.
        </Callout>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="primary" size="sm" onClick={onHide}>
          Entendi!
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DescriptionTemplateGuideModal;
